import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const block = (e) => e.preventDefault();

const useHideOnCapture = () => {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const hide = () => setHidden(true);
    const show = () => setHidden(false);
    const onKey = (e) => {
      if (e.key === "PrintScreen" || (e.metaKey && e.shiftKey) || ((e.ctrlKey || e.metaKey) && ["p", "s"].includes(e.key.toLowerCase()))) {
        e.preventDefault();
        hide();
        setTimeout(show, 1500);
      }
    };
    window.addEventListener("blur", hide);
    window.addEventListener("focus", show);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("blur", hide); window.removeEventListener("focus", show); window.removeEventListener("keydown", onKey); };
  }, []);
  return hidden;
};

export const ResumePreview = ({ resumeId, version, unlocked }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const hidden = useHideOnCapture();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    api.get(`/resumes/${resumeId}/preview`, { responseType: "blob", signal: ctrl.signal })
      .then((r) => setSrc((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); }))
      .catch((e) => { if (!ctrl.signal.aborted) setError(true); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [resumeId, version]);

  return (
    <div className="protected relative rounded-xl overflow-hidden bg-[#1a1d27] border border-white/10"
      onContextMenu={block} onCopy={block} onDragStart={block} data-testid="resume-preview">
      {src && <img src={src} alt="Resume preview" draggable={false} data-testid="resume-preview-image"
        className={`w-full block transition-[filter,opacity] duration-200 ${!unlocked && hidden ? "opacity-0" : ""}`} />}
      {error && <p className="p-10 text-center text-sm text-red-400" data-testid="preview-error">Preview could not be rendered. Keep editing and it will retry.</p>}
      {loading && (
        <div className="absolute top-3 right-3 flex items-center gap-2 text-xs font-mono text-slate-300 bg-black/60 backdrop-blur rounded-full px-3 py-1.5" data-testid="preview-loading">
          <Loader2 className="h-3 w-3 animate-spin" /> Rendering
        </div>
      )}
      {!unlocked && (
        <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/90 via-black/70 to-transparent" data-testid="resume-preview-lock-overlay">
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#0c0e14]/90 backdrop-blur-xl p-5">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-full bg-amber-500/15 text-amber-400 flex items-center justify-center"><Lock className="h-4 w-4" /></span>
              <div>
                <p className="font-semibold text-sm">Watermarked preview</p>
                <p className="text-xs text-slate-400">Unlock to remove the watermark and download a crisp PDF.</p>
              </div>
            </div>
            <Link to="/pricing" data-testid="preview-unlock-btn" className="rounded-full bg-emerald-500 text-black text-sm font-semibold px-5 py-2.5 hover:bg-emerald-400 transition-colors">
              Unlock from $2.99
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
